import { api } from './client';

export type RoleName = 'ADMIN' | 'MANAGER' | 'MEMBER' | 'OBSERVER';
export type UserStatus = 'PENDING' | 'ACTIVE' | 'DISABLED';

export type ThemePref = 'light' | 'dark' | 'system';

export interface MeDto {
  id: string;
  email: string;
  displayName: string;
  role: RoleName;
  status: UserStatus;
  avatarUrl: string | null;
  timezone: string | null;
  theme: ThemePref;
  jobTitle: string | null;
  roleDescription: string | null;
  managerId: string | null;
  /**
   * Comma-separated list of Tricorian sub-company allocations this user can
   * view (TC_LIFE | VELENDA | COR42). Null = sees everything (admin/master).
   * Used by the renderer to filter which options appear in allocation
   * dropdowns and to auto-default to the first allowed scope.
   */
  allocationScopes: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

/**
 * Lightweight user record exposed to any authenticated user via
 * `GET /api/users/directory`. Used by pickers (board membership, card
 * assignment) where the heavier UserAdminDto would be both over-privileged
 * and overkill.
 */
export interface DirectoryUserDto {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  status: UserStatus;
}

export interface UserAdminDto {
  id: string;
  email: string;
  displayName: string;
  role: RoleName;
  status: UserStatus;
  avatarUrl?: string | null;
  jobTitle?: string | null;
  roleDescription?: string | null;
  managerId?: string | null;
  timezone?: string | null;
  allocationScopes?: string | null;
  lastLoginAt: string | null;
  failedAttempts: number;
  lockedUntil: string | null;
  createdAt?: string;
}

export interface OrgPositionDto {
  id: string;
  jobTitle: string;
  roleDescription: string | null;
  managerId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OrgSnapshotDto {
  users: Array<
    Pick<
      UserAdminDto,
      'id' | 'email' | 'displayName' | 'role' | 'jobTitle' | 'roleDescription' | 'managerId' | 'avatarUrl'
    >
  >;
  positions: OrgPositionDto[];
}

export interface InviteDto {
  id: string;
  email: string;
  role: RoleName;
  status: 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';
  expiresAt: string;
  createdAt: string;
  redeemUrl?: string;
}

export interface AuditEntryDto {
  id: string;
  actorId: string | null;
  /** Hydrated by the server when the actor still exists. */
  actor: { id: string; email: string; displayName: string } | null;
  action: string;
  resource: string;
  resourceId: string | null;
  ip: string | null;
  createdAt: string;
}

export interface UpdateMeBody {
  displayName?: string;
  avatarUrl?: string | null;
  timezone?: string | null;
  theme?: ThemePref;
  jobTitle?: string | null;
  roleDescription?: string | null;
}

export interface UpdateUserBody {
  role?: RoleName;
  status?: 'ACTIVE' | 'DISABLED' | 'PENDING';
  jobTitle?: string | null;
  roleDescription?: string | null;
  managerId?: string | null;
  /**
   * Set to an array of allowed allocations, or null to mean "sees all".
   * Omit the key entirely to leave the existing value unchanged.
   */
  allocationScopes?: ('TC_LIFE' | 'VELENDA' | 'COR42')[] | null;
}

export interface CreateUserBody {
  email: string;
  displayName: string;
  role?: RoleName;
  jobTitle?: string;
  allocationScopes?: ('TC_LIFE' | 'VELENDA' | 'COR42')[] | null;
}

export const identityApi = {
  me: () => api<MeDto>('/me'),
  updateMe: (body: UpdateMeBody) =>
    api<MeDto>('/me', { method: 'PATCH', body }),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    api<{ ok: true }>('/me/change-password', { method: 'POST', body }),

  listUsers: (params?: { role?: RoleName; status?: UserStatus; q?: string }) =>
    api<{ items: UserAdminDto[] }>('/users', { query: params }),
  /**
   * Minimal user list usable by any authenticated user (no admin required).
   * Drives the board-membership and card-assignment pickers in the kanban
   * module.
   */
  listDirectory: () => api<{ items: DirectoryUserDto[] }>('/users/directory'),
  createUser: (body: CreateUserBody) =>
    api<UserAdminDto>('/users', { method: 'POST', body }),
  updateUser: (id: string, body: UpdateUserBody) =>
    api<UserAdminDto>(`/users/${id}`, { method: 'PATCH', body }),
  unlockUser: (id: string) =>
    api<UserAdminDto>(`/users/${id}/unlock`, { method: 'POST' }),
  disableUser: (id: string) => api<void>(`/users/${id}`, { method: 'DELETE' }),
  hardDeleteUser: (id: string) =>
    api<void>(`/users/${id}/hard-delete`, { method: 'POST' }),

  listInvites: () => api<{ items: InviteDto[] }>('/invites'),
  createInvite: (body: { email: string; role: RoleName }) =>
    api<InviteDto>('/invites', { method: 'POST', body }),
  revokeInvite: (id: string) => api<void>(`/invites/${id}`, { method: 'DELETE' }),

  audit: (params?: { limit?: number; resource?: string }) =>
    api<{ items: AuditEntryDto[] }>('/audit', { query: params }),

  // Org chart: combined users + vacant positions snapshot.
  org: () => api<OrgSnapshotDto>('/org'),
  createPosition: (body: {
    jobTitle: string;
    roleDescription?: string | null;
    managerId?: string | null;
  }) => api<OrgPositionDto>('/org/positions', { method: 'POST', body }),
  updatePosition: (
    id: string,
    body: {
      jobTitle?: string;
      roleDescription?: string | null;
      managerId?: string | null;
    },
  ) => api<OrgPositionDto>(`/org/positions/${id}`, { method: 'PATCH', body }),
  deletePosition: (id: string) =>
    api<void>(`/org/positions/${id}`, { method: 'DELETE' }),
};
